import React, { useState, useEffect } from "react";
import { makeStyles } from "@mui/styles";
import { Paper, Typography, TextField, Button, Chip, IconButton, Divider, CircularProgress } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import InputAdornment from "@mui/material/InputAdornment";

import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import MainContainer from "../../components/MainContainer";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const TAG_COLORS = [
  "#F37021", "#273A74", "#0071BC", "#C23582",
  "#F4C02F", "#555A5F", "#27AE60", "#E74C3C",
  "#8E44AD", "#16A085", "#D35400", "#2C3E50",
];

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
    maxWidth: 640,
  },
  addSection: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
  addRow: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
  },
  colorRow: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
    alignItems: "center",
  },
  colorDot: {
    width: 22,
    height: 22,
    borderRadius: "50%",
    cursor: "pointer",
    border: "2px solid transparent",
    flexShrink: 0,
    "&:hover": { opacity: 0.8 },
  },
  tagsSection: {
    padding: theme.spacing(2),
  },
  tagsList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
  },
  tagRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "6px 8px",
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
  },
  tagLeft: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  colorSwatch: {
    width: 14,
    height: 14,
    borderRadius: "50%",
    flexShrink: 0,
  },
  emptyText: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    marginTop: theme.spacing(1),
  },
}));

const Tags = () => {
  const classes = useStyles();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#F37021");
  const [saving, setSaving] = useState(false);
  const [filterText, setFilterText] = useState("");

  useEffect(() => {
    api.get("/tags/global")
      .then(({ data }) => setTags(data))
      .catch(err => toastError(err, "Página de Tags — carregar lista"))
      .finally(() => setLoading(false));
  }, []);

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    try {
      const { data } = await api.post("/tags/global", { name, color: newColor });
      setTags(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewName("");
      setNewColor("#F37021");
      toast.success("Tag criada com sucesso");
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.warn("Já existe uma tag com esse nome");
      } else {
        toastError(err);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tagId) => {
    try {
      await api.delete(`/tags/global/${tagId}`);
      setTags(prev => prev.filter(t => t.id !== tagId));
      toast.success("Tag removida");
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Tags Globais</Title>
        <MainHeaderButtonsWrapper />
      </MainHeader>

      <div className={classes.container}>
        <Paper variant="outlined" className={classes.addSection}>
          <Typography variant="subtitle2">Nova tag</Typography>
          <div className={classes.addRow}>
            <TextField
              size="small"
              variant="outlined"
              placeholder="Nome da tag..."
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyPress={e => e.key === "Enter" && handleAdd()}
              style={{ flex: 1 }}
            />
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
              onClick={handleAdd}
              disabled={saving || !newName.trim()}
            >
              Adicionar
            </Button>
          </div>
          <div className={classes.colorRow}>
            <Typography variant="caption" style={{ marginRight: 4 }}>Cor:</Typography>
            {TAG_COLORS.map(c => (
              <div
                key={c}
                className={classes.colorDot}
                style={{
                  backgroundColor: c,
                  border: newColor === c ? "2px solid #333" : "2px solid transparent",
                }}
                onClick={() => setNewColor(c)}
              />
            ))}
            <Chip
              size="small"
              label={newName || "Prévia"}
              style={{ backgroundColor: newColor, color: "#fff", fontWeight: 600, fontSize: 11 }}
            />
          </div>
        </Paper>

        <Paper variant="outlined" className={classes.tagsSection}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <LocalOfferIcon style={{ fontSize: 18, color: "#F37021" }} />
            <Typography variant="subtitle2">Tags cadastradas</Typography>
            <Typography variant="caption" style={{ marginLeft: "auto" }}>
              {tags.length} {tags.length === 1 ? "tag" : "tags"}
            </Typography>
          </div>
          <Divider style={{ margin: "8px 0" }} />
          {tags.length > 5 && (
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              placeholder="Filtrar tags..."
              value={filterText}
              onChange={e => setFilterText(e.target.value)}
              style={{ marginBottom: 10 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" style={{ color: "gray" }} />
                  </InputAdornment>
                ),
                endAdornment: filterText ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setFilterText("")} edge="end">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          )}
          {loading ? (
            <CircularProgress size={24} style={{ display: "block", margin: "16px auto" }} />
          ) : tags.length === 0 ? (
            <Typography className={classes.emptyText}>
              Nenhuma tag global cadastrada. Crie acima para que apareçam como sugestão em todos os contatos.
            </Typography>
          ) : (
            <div className={classes.tagsList}>
              {tags.filter(tag => !filterText || tag.name.toLowerCase().includes(filterText.toLowerCase())).map(tag => (
                <div key={tag.id} className={classes.tagRow}>
                  <div className={classes.tagLeft}>
                    <div className={classes.colorSwatch} style={{ backgroundColor: tag.color }} />
                    <Typography variant="body2">{tag.name}</Typography>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Chip
                      size="small"
                      label={tag.name}
                      style={{ backgroundColor: tag.color, color: "#fff", fontWeight: 600, fontSize: 11 }}
                    />
                    <IconButton size="small" onClick={() => handleDelete(tag.id)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Paper>

        <Typography variant="caption" style={{ display: "block", marginTop: 16, color: "#8A9096" }}>
          Tags globais aparecem como sugestão no Autocomplete ao adicionar tags em qualquer contato.
          Tags de contato individuais continuam funcionando normalmente.
        </Typography>
      </div>
    </MainContainer>
  );
};

export default Tags;
